import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { getClientTheme } from "../../clientConfig";

const clientTheme = getClientTheme();

export type ThemeMode = "light" | "dark";

const createAppTheme = (palette: typeof clientTheme.palette.light) => {
  const theme = createTheme({
    cssVariables: true,
    shape: {
      borderRadius: 8,
    },
    palette,
    typography: clientTheme.typography,
  });

  theme.components = {
    ...theme.components,
   
  };

  return theme;
};

export const lightTheme = createAppTheme(clientTheme.palette.light);
export const darkTheme = createAppTheme(clientTheme.palette.dark);

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const THEME_STORAGE_KEY = "theme-mode";

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return (stored as ThemeMode) || "light";
    } catch {
      return "light";
    }
  });

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (error) {
      console.error("Failed to save theme to localStorage:", error);
    }
  }, [mode]);

  const currentTheme = mode === "light" ? lightTheme : darkTheme;

  const value: ThemeContextType = {
    mode,
    toggleTheme,
    setTheme,
  };

  return React.createElement(
    ThemeContext.Provider,
    { value },
    React.createElement(
      MuiThemeProvider,
      { theme: currentTheme },
      React.createElement(CssBaseline),
      children
    )
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
